import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemBanco } from '../mensagemBanco';
import { ehCancelada, type FiltroComo } from '../presencaFormacao';
import CardAguardando from './CardAguardando';
import CaixaQRChamada from './CaixaQRChamada';
import FolhaCancelarPresenca from './FolhaCancelarPresenca';
import LinhaPresenca from './LinhaPresenca';
import ListaTodosPresenca from './ListaTodosPresenca';
import ModalConfirmarAcao from './ModalConfirmarAcao';
import ModalHistoricoPresenca from './ModalHistoricoPresenca';
import type { InscritoPresenca } from '../types';

interface Props {
  encontroId: number;
  formacao: string;
  formacaoQuando?: string | null;
  aoVoltar: () => void;
}

type Estado = 'carregando' | 'pronto' | 'erro';

const CHIPS: { valor: FiltroComo; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'qr', rotulo: 'Pelo QR' },
  { valor: 'foto', rotulo: 'Por foto' },
  { valor: 'direcao', rotulo: 'Pela direção' },
  { valor: 'canceladas', rotulo: 'Canceladas' },
];
type FiltroPresenca = 'todos' | 'aguardando' | 'recusadas' | 'presentes' | 'faltas';

interface Contador {
  valor: FiltroPresenca;
  rotulo: string;
  corNumero: string;
  corAtiva: string;
  destacado: boolean;
}

const CONTADORES: Contador[] = [
  {
    valor: 'aguardando',
    rotulo: 'aguardando você',
    corNumero: 'text-amber-700',
    corAtiva: 'border-amber-300 bg-amber-50',
    destacado: true,
  },
  {
    valor: 'presentes',
    rotulo: 'confirmadas',
    corNumero: 'text-green-700',
    corAtiva: 'border-green-300 bg-green-50',
    destacado: false,
  },
  {
    valor: 'recusadas',
    rotulo: 'recusadas',
    corNumero: 'text-red-700',
    corAtiva: 'border-red-300 bg-red-50',
    destacado: false,
  },
  {
    valor: 'faltas',
    rotulo: 'ainda faltam',
    corNumero: 'text-secondary-600',
    corAtiva: 'border-secondary-300 bg-secondary-50',
    destacado: false,
  },
];

export default function PresencasFormacao({ encontroId, formacao, formacaoQuando, aoVoltar }: Props) {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<InscritoPresenca[]>([]);

  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<FiltroPresenca>('todos');
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  // Aprovação / recusa.
  const [avaliandoId, setAvaliandoId] = useState<number | null>(null);

  // Liberar mais uma tentativa.
  const [liberandoId, setLiberandoId] = useState<number | null>(null);

  // Marcar presença manual.
  const [salvandoManual, setSalvandoManual] = useState(false);

  // Foto ampliada.
  const [ampliada, setAmpliada] = useState<string | null>(null);

  // Histórico.
  const [historicoPara, setHistoricoPara] = useState<{
    inscricaoId: number;
    nome: string;
  } | null>(null);

  // Filtro rápido por como a presença foi marcada.
  const [filtroComo, setFiltroComo] = useState<FiltroComo>('todas');

  // Cancelar presença.
  const [cancelarPara, setCancelarPara] = useState<InscritoPresenca | null>(null);
  const [salvandoCancelar, setSalvandoCancelar] = useState(false);
  const [erroCancelar, setErroCancelar] = useState<string | null>(null);

  // Desfazer cancelamento.
  const [desfazerPara, setDesfazerPara] = useState<InscritoPresenca | null>(null);
  const [salvandoDesfazer, setSalvandoDesfazer] = useState(false);

  async function carregar() {
    const { data, error } = await supabase.rpc('presencas_da_formacao', {
      p_encontro: encontroId,
    });
    if (error) throw error;
    setLinhas((data as InscritoPresenca[]) || []);
  }

  useEffect(() => {
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        await carregar();
        if (ativo) setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemBanco(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encontroId]);

  const contagem = useMemo(
    () => ({
      aguardando: linhas.filter((l) => l.situacao === 'aguardando').length,
      recusadas: linhas.filter((l) => l.situacao === 'recusada').length,
      presentes: linhas.filter((l) => l.situacao === 'confirmada').length,
      faltas: linhas.filter((l) => l.situacao === 'falta').length,
    }),
    [linhas],
  );

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    let base = linhas.filter((l) => !termo || l.nome.toLowerCase().includes(termo));
    switch (filtro) {
      case 'aguardando':
        base = base.filter((l) => l.situacao === 'aguardando');
        break;
      case 'recusadas':
        base = base.filter((l) => l.situacao === 'recusada');
        break;
      case 'presentes':
        base = base.filter((l) => l.situacao === 'confirmada');
        break;
      case 'faltas':
        base = base.filter((l) => l.situacao === 'falta');
        break;
      default:
        break;
    }
    switch (filtroComo) {
      case 'qr':
        base = base.filter((l) => l.como === 'qr');
        break;
      case 'foto':
        base = base.filter((l) => l.como === 'foto');
        break;
      case 'direcao':
        base = base.filter((l) => l.como === 'direcao');
        break;
      case 'canceladas':
        base = base.filter((l) => ehCancelada(l));
        break;
      default:
        break;
    }
    return base;
  }, [busca, filtro, filtroComo, linhas]);

  const contagemChips = useMemo(
    () => ({
      todas: linhas.length,
      qr: linhas.filter((l) => l.como === 'qr').length,
      foto: linhas.filter((l) => l.como === 'foto').length,
      direcao: linhas.filter((l) => l.como === 'direcao').length,
      canceladas: linhas.filter((l) => ehCancelada(l)).length,
    }),
    [linhas],
  );

  async function avaliar(presencaId: number, aprovar: boolean, obs?: string) {
    setAvaliandoId(presencaId);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('avaliar_presenca', {
        p_id: presencaId,
        p_aprovar: aprovar,
        p_obs: obs ?? null,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível avaliar. Tente de novo.');
        return;
      }
      await carregar();
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setAvaliandoId(null);
    }
  }

  async function liberarReenvio(presencaId: number) {
    setLiberandoId(presencaId);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('liberar_reenvio_presenca', {
        p_id: presencaId,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível liberar. Tente de novo.');
        return;
      }
      await carregar();
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setLiberandoId(null);
    }
  }

  async function marcarManual(inscricaoId: number, obs: string) {
    setSalvandoManual(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('marcar_presenca_manual', {
        p_encontro: encontroId,
        p_inscricao: inscricaoId,
        p_obs: obs || null,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível marcar. Tente de novo.');
        return;
      }
      await carregar();
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setSalvandoManual(false);
    }
  }

  function abrirHistorico(inscricaoId: number, nome: string) {
    setHistoricoPara({ inscricaoId, nome });
  }

  function abrirCancelar(linha: InscritoPresenca) {
    setErroCancelar(null);
    setCancelarPara(linha);
  }

  function abrirDesfazer(linha: InscritoPresenca) {
    setErroAcao(null);
    setDesfazerPara(linha);
  }

  async function confirmarCancelamento(motivo: string) {
    if (!cancelarPara?.presenca_id) return;
    setSalvandoCancelar(true);
    setErroCancelar(null);
    try {
      const { data, error } = await supabase.rpc('cancelar_presenca', {
        p_presenca: cancelarPara.presenca_id,
        p_motivo: motivo,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; mensagem?: string; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroCancelar(resp?.erro || 'Não foi possível cancelar. Tente de novo.');
        return;
      }
      mostrarToast(resp.mensagem || 'Presença cancelada.', 4000, 'sucesso');
      setCancelarPara(null);
      await carregar();
    } catch (e) {
      setErroCancelar(mensagemBanco(e));
    } finally {
      setSalvandoCancelar(false);
    }
  }

  async function confirmarDesfazer() {
    if (!desfazerPara?.presenca_id) return;
    setSalvandoDesfazer(true);
    setErroAcao(null);
    try {
      const { data, error } = await supabase.rpc('desfazer_cancelamento_presenca', {
        p_presenca: desfazerPara.presenca_id,
      });
      if (error) throw error;
      const resp = data as { ok: boolean; mensagem?: string; erro?: string } | null;
      if (!resp || resp.ok === false) {
        setErroAcao(resp?.erro || 'Não foi possível desfazer. Tente de novo.');
        return;
      }
      mostrarToast(resp.mensagem || 'Presença restaurada.', 4000, 'sucesso');
      setDesfazerPara(null);
      await carregar();
    } catch (e) {
      setErroAcao(mensagemBanco(e));
    } finally {
      setSalvandoDesfazer(false);
    }
  }

  if (estado === 'carregando') {
    return (
      <div>
        <div className="h-10 w-72 animate-pulse rounded bg-secondary-100" />
        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-secondary-100" />
          ))}
        </div>
        <div className="mt-4 h-64 animate-pulse rounded-lg bg-secondary-100" />
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={aoVoltar}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          Voltar à lista
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={aoVoltar}
        className="flex cursor-pointer items-center gap-2 text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-left-line" />
        </span>
        Voltar
      </button>

      <h2 className="mt-3 text-xl font-normal text-foreground-950">{formacao}</h2>

      {erroAcao && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {erroAcao}
        </div>
      )}

      <div className="mt-4">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-secondary-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome…"
            className="w-full rounded-md border border-secondary-300 bg-white py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none placeholder:text-secondary-400 focus:border-primary-400"
          />
        </div>
      </div>

      {/* Botão Ver todos */}
      <button
        type="button"
        onClick={() => setFiltro('todos')}
        className={`mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${
          filtro === 'todos'
            ? 'border-primary-400 bg-primary-50 text-primary-700'
            : 'border-secondary-200 bg-white text-foreground-800 hover:bg-background-100'
        }`}
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-list-check-2 text-base" />
        </span>
        Ver todos · {linhas.length}
      </button>

      {/* Contadores clicáveis */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        {CONTADORES.map((c) => {
          const ativo = filtro === c.valor;
          const destacado = c.destacado;
          return (
            <button
              key={c.valor}
              type="button"
              onClick={() => setFiltro(c.valor)}
              className={`flex min-w-0 cursor-pointer flex-col items-start gap-1 rounded-2xl border px-4 py-3 text-left transition-colors ${
                destacado || ativo
                  ? c.corAtiva
                  : 'border-secondary-200 bg-white hover:bg-background-100'
              }`}
            >
              <span
                className={`text-3xl font-bold leading-none ${
                  destacado || ativo ? c.corNumero : 'text-foreground-950'
                }`}
              >
                {contagem[c.valor]}
              </span>
              <span className="text-xs leading-tight text-secondary-500">{c.rotulo}</span>
            </button>
          );
        })}
      </div>

      {/* Filtro rápido no topo da lista */}
      <div className="mt-6 flex flex-wrap gap-2">
        {CHIPS.map((c) => {
          const ativo = filtroComo === c.valor;
          return (
            <button
              key={c.valor}
              type="button"
              onClick={() => setFiltroComo(c.valor)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                ativo
                  ? 'border-primary-400 bg-primary-50 text-primary-700'
                  : 'border-secondary-300 bg-white text-foreground-700 hover:bg-background-100'
              }`}
            >
              {c.rotulo} · {contagemChips[c.valor]}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {filtro === 'todos' && (
          <ListaTodosPresenca
            linhas={filtradas}
            salvandoManual={salvandoManual}
            avaliandoId={avaliandoId}
            liberandoId={liberandoId}
            aoAvaliar={avaliar}
            aoLiberarReenvio={liberarReenvio}
            aoMarcar={marcarManual}
            aoAbrirHistorico={abrirHistorico}
            aoAmpliar={setAmpliada}
            formacaoQuando={formacaoQuando}
            aoCancelar={abrirCancelar}
            aoDesfazer={abrirDesfazer}
            desfazendoId={salvandoDesfazer ? (desfazerPara?.presenca_id ?? null) : null}
          />
        )}

        {filtro === 'aguardando' && (
          <>
            <CaixaQRChamada encontroId={encontroId} />

            {filtradas.length === 0 ? (
              <p className="mt-4 text-sm text-secondary-500">Nenhuma foto esperando.</p>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {filtradas.map((l) => (
                  <CardAguardando
                    key={l.inscricao_id}
                    linha={l}
                    avaliando={avaliandoId === l.presenca_id}
                    liberando={liberandoId === l.presenca_id}
                    aoConfirmar={() => avaliar(l.presenca_id as number, true)}
                    aoRecusar={(motivo) => avaliar(l.presenca_id as number, false, motivo)}
                    aoLiberarReenvio={() => liberarReenvio(l.presenca_id as number)}
                    aoAbrirHistorico={() => abrirHistorico(l.inscricao_id, l.nome)}
                    aoAmpliar={setAmpliada}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {(filtro === 'presentes' || filtro === 'recusadas' || filtro === 'faltas') && (
          <>
            {filtradas.length === 0 ? (
              <p className="text-sm text-secondary-500">
                {filtro === 'presentes'
                  ? 'Ninguém confirmado ainda.'
                  : filtro === 'recusadas'
                    ? 'Ninguém recusado.'
                    : 'Ninguém faltando.'}
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
                {filtradas.map((l, i) => (
                  <div
                    key={l.inscricao_id}
                    className={i > 0 ? 'border-t border-secondary-100' : ''}
                  >
                    <LinhaPresenca
                      linha={l}
                      tipo={
                        filtro === 'presentes' ? 'presente' : filtro === 'recusadas' ? 'recusada' : 'falta'
                      }
                      salvando={salvandoManual}
                      liberando={liberandoId === l.presenca_id}
                      aoMarcar={marcarManual}
                      aoLiberarReenvio={liberarReenvio}
                      formacaoQuando={formacaoQuando}
                      aoCancelar={abrirCancelar}
                      aoDesfazer={abrirDesfazer}
                      desfazendo={salvandoDesfazer && desfazerPara?.presenca_id === l.presenca_id}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {ampliada && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setAmpliada(null)}
        >
          <img
            src={ampliada}
            alt="Foto ampliada"
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </div>
      )}

      {historicoPara && (
        <ModalHistoricoPresenca
          encontroId={encontroId}
          inscricaoId={historicoPara.inscricaoId}
          nome={historicoPara.nome}
          aoFechar={() => setHistoricoPara(null)}
        />
      )}

      {cancelarPara && (
        <FolhaCancelarPresenca
          linha={cancelarPara}
          formacaoQuando={formacaoQuando}
          salvando={salvandoCancelar}
          erro={erroCancelar}
          aoVoltar={() => setCancelarPara(null)}
          aoConfirmar={confirmarCancelamento}
        />
      )}

      {desfazerPara && (
        <ModalConfirmarAcao
          titulo="Desfazer o cancelamento"
          mensagem={`A presença de ${desfazerPara.nome} volta a valer?`}
          textoConfirmar="Desfazer"
          salvando={salvandoDesfazer}
          aoConfirmar={confirmarDesfazer}
          aoCancelar={() => setDesfazerPara(null)}
        />
      )}
    </div>
  );
}